/**
 * A vitrola da casa: o `jukebox_provider.dart` e o `music_stats_provider.dart`.
 *
 * Os dois saem chaveados por **slug** e não pelo nome de usuário do Last.fm,
 * porque é o slug que a tela conhece — o nome de usuário é detalhe dos ajustes.
 *
 * Quem consulta o Last.fm é todo aparelho aberto, para **todo mundo da casa**, e
 * o resultado vai para o armazém. Não é desperdício: é o que mantém as telas
 * iguais, porque quem está com o app aberto vê o scrobble da outra pessoa sem
 * esperar a própria consulta. O armazém é a fonte da verdade; a consulta só o
 * alimenta.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ligacao } from '../nucleo/ligacao';
import { usarAjustes } from '../nucleo/ajustes';
import { usarColecao } from '../nucleo/usarArmazem';
import {
  COLECAO_MUSICA,
  type EstadoDeMusica,
  type FaixaMaisTocada,
  estadoDeFaixa,
  estadoDeMapa,
  estadoParaMapa,
  estadoVazio,
  faixaMaisTocadaDeJson,
} from './estado';
import { capaDaFaixa, faixasMaisTocadas, faixasRecentes } from './lastfm';

/** De quanto em quanto tempo cada coisa se repete, como no Flutter. */
const A_CADA_TOQUE = 30_000;
const A_CADA_RECENTE = 60_000;
const A_CADA_TOP = 600_000;

const QUANTOS_TOPS = 10;
const QUANTOS_RECENTES = 5;

export interface OuvindoAgora {
  slug: string;
  usuario: string;
  estado: EstadoDeMusica;
}

/**
 * Uma chave estável para o efeito: sem ela o `usarAjustes` devolve um objeto
 * novo a cada aviso e o relógio de 30 s recomeçaria do zero para sempre.
 */
const assinatura = (contas: Record<string, string>): string =>
  Object.keys(contas)
    .sort()
    .map((slug) => `${slug}:${contas[slug]}`)
    .join('|');

/** Quem vinculou o Last.fm, na ordem em que os ajustes os têm. */
function contasVinculadas(contas: Record<string, string>): Array<[string, string]> {
  return Object.entries(contas).filter(([, usuario]) => usuario.trim() !== '');
}

/** O que cada pessoa da casa está ouvindo agora. */
export function usarJukebox(): OuvindoAgora[] {
  const ajustes = usarAjustes();
  const documentos = usarColecao<Record<string, unknown>>(COLECAO_MUSICA);
  const contas = ajustes.lastfm;
  const chave = ajustes.lastfmKey;
  const marca = assinatura(contas);

  useEffect(() => {
    const vinculadas = contasVinculadas(contas);
    if (!chave || vinculadas.length === 0) return;

    const { armazem, aoVivo } = ligacao();
    let vivo = true;

    const rodada = async () => {
      const escritas = await Promise.all(
        vinculadas.map(async ([, usuario]) => {
          const faixas = await faixasRecentes(usuario, chave, 1);
          if (!vivo || faixas.length === 0) return false;
          const estado = estadoDeFaixa(faixas[0], usuario);
          await armazem.gravar(COLECAO_MUSICA, usuario, estadoParaMapa(estado)).catch(() => false);
          return true;
        }),
      );
      // Sem fluxo (celular) o aparelho não vê a própria gravação voltar; uma
      // recarga por rodada é o que fecha o ciclo, e só quando houve gravação.
      if (vivo && !aoVivo && escritas.some(Boolean)) {
        await armazem.carregar(COLECAO_MUSICA).catch(() => {});
      }
    };

    void rodada();
    const relogio = setInterval(() => void rodada(), A_CADA_TOQUE);
    return () => {
      vivo = false;
      clearInterval(relogio);
    };
    // `contas` entra pela `marca`: a referência muda a cada aviso dos ajustes,
    // o conteúdo quase nunca.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marca, chave]);

  return useMemo(() => {
    const gravados = new Map(documentos.map((d) => [d.id, d.dados]));
    return contasVinculadas(contas).map(([slug, usuario]) => {
      const bruto = gravados.get(usuario);
      return {
        slug,
        usuario,
        estado: bruto ? estadoDeMapa(bruto) : estadoVazio(usuario),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentos, marca]);
}

export interface EstatisticasDeAlguem {
  slug: string;
  usuario: string;
  tops: FaixaMaisTocada[];
  recentes: EstadoDeMusica[];
}

export interface EstatisticasDaVitrola {
  pessoas: EstatisticasDeAlguem[];
  /**
   * Verdadeiro até a primeira rodada terminar, e não em toda recarga: o
   * `isLoading` do `music_stats_provider.dart` só aparece na tela junto com o
   * `!hasData`, e as rodadas seguintes trocam a lista sem piscar.
   */
  carregando: boolean;
}

/**
 * As capas que já se procurou uma vez.
 *
 * Guarda o `null` também: uma faixa sem capa em lugar nenhum não pode custar
 * duas consultas a cada dez minutos, para sempre. Vive enquanto o app viver.
 */
const capas = new Map<string, string | null>();

async function capaMemorizada(
  artista: string,
  faixa: string,
  chave: string,
  mbid?: string | null,
): Promise<string | null> {
  const memoria = `${artista} ${faixa}`;
  const guardada = capas.get(memoria);
  if (guardada !== undefined) return guardada;
  const achada = await capaDaFaixa(artista, faixa, chave, mbid);
  capas.set(memoria, achada);
  return achada;
}

/**
 * Os mais tocados de sempre e os últimos cinco scrobbles de cada um.
 *
 * Estes não passam pelo armazém: são leitura pura do Last.fm, não interessam a
 * ninguém depois de fechado o app, e gravá-los encheria a coleção de histórico
 * que já existe do lado de lá. Por isso as cadências são mais largas que a da
 * vitrola — os mais tocados de sempre não mudam de dez em dez minutos.
 */
export function usarEstatisticasDeMusica(): EstatisticasDaVitrola {
  const ajustes = usarAjustes();
  const contas = ajustes.lastfm;
  const chave = ajustes.lastfmKey;
  const marca = assinatura(contas);

  const [tops, definirTops] = useState<Record<string, FaixaMaisTocada[]>>({});
  const [recentes, definirRecentes] = useState<Record<string, EstadoDeMusica[]>>({});
  const [carregando, definirCarregando] = useState(true);
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  useEffect(() => {
    const vinculadas = contasVinculadas(contas);
    if (!chave || vinculadas.length === 0) {
      definirTops({});
      definirRecentes({});
      definirCarregando(false);
      return;
    }

    const buscarRecentes = async () => {
      for (const [slug, usuario] of vinculadas) {
        const faixas = await faixasRecentes(usuario, chave, QUANTOS_RECENTES);
        if (!vivo.current) return;
        definirRecentes((antes) => ({
          ...antes,
          [slug]: faixas.map((f) => estadoDeFaixa(f, usuario)),
        }));
      }
    };

    const buscarTops = async () => {
      for (const [slug, usuario] of vinculadas) {
        const cruas = await faixasMaisTocadas(usuario, chave, QUANTOS_TOPS);
        if (!vivo.current) return;
        // O Last.fm às vezes manda `@attr.rank` vazio; a posição da lista já é
        // a classificação, então ela serve de reserva.
        const faixas = cruas.map((f, i) => {
          const faixa = faixaMaisTocadaDeJson(f);
          return faixa.rank > 0 ? faixa : { ...faixa, rank: i + 1 };
        });
        definirTops((antes) => ({ ...antes, [slug]: faixas }));
        void enfeitar(slug, faixas);
      }
    };

    // A capa chega depois da lista, uma a uma: esperar as dez deixaria a seção
    // vazia por segundos, e o pódio já vale sem imagem.
    const enfeitar = async (slug: string, faixas: FaixaMaisTocada[]) => {
      for (const faixa of faixas) {
        if (faixa.imageUrl) continue;
        const capa = await capaMemorizada(faixa.artistName, faixa.trackName, chave, faixa.mbid);
        if (!vivo.current || !capa) continue;
        definirTops((antes) => {
          const lista = antes[slug];
          // Se a lista já foi trocada por outra rodada, esta capa é de um
          // pedido velho e não tem onde entrar.
          if (!lista || !lista.includes(faixa)) return antes;
          return {
            ...antes,
            [slug]: lista.map((f) => (f === faixa ? { ...f, imageUrl: capa } : f)),
          };
        });
      }
    };

    // O `enfeitar` fica de fora da conta de propósito: a capa que chega depois
    // não pode segurar a seção inteira no esqueleto.
    void Promise.all([buscarRecentes(), buscarTops()]).then(() => {
      if (vivo.current) definirCarregando(false);
    });
    const relogioRecentes = setInterval(() => void buscarRecentes(), A_CADA_RECENTE);
    const relogioTops = setInterval(() => void buscarTops(), A_CADA_TOP);
    return () => {
      clearInterval(relogioRecentes);
      clearInterval(relogioTops);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marca, chave]);

  return useMemo(
    () => ({
      pessoas: contasVinculadas(contas).map(([slug, usuario]) => ({
        slug,
        usuario,
        tops: tops[slug] ?? [],
        recentes: recentes[slug] ?? [],
      })),
      carregando,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [marca, tops, recentes, carregando],
  );
}
